import { Check, Package, Pencil, X } from 'lucide-react-native';
import { useState } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { FormInput, HStack, Text, VStack } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { LeadProductReference } from '../../../types/leads';

interface ProductInterestFieldProps {
  value?: string | null;
  valueReference?: LeadProductReference | null;
  suggestion?: string | null;
  suggestionReference?: LeadProductReference | null;
  suggestionConfidence?: 'high' | 'low' | null;
  onSave: (next: string, productId?: string) => void;
  isSaving?: boolean;
}

const asText = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return String(value);
  return '';
};

const displayName = (
  storedText: unknown,
  reference: LeadProductReference | null | undefined,
): string => {
  if (reference?.available === true) return reference.name;
  return asText(storedText) || reference?.name || '';
};

const availableKind = (
  reference: LeadProductReference | null | undefined,
): LeadProductReference['kind'] => (reference?.available === true ? reference.kind : undefined);

function KindBadge({ labelTestID, label }: Readonly<{ labelTestID: string; label: string }>) {
  return (
    <View style={styles.kindBadge} accessibilityElementsHidden importantForAccessibility="no">
      <Text testID={labelTestID} variant="caption" color={colors.text.secondary}>
        {label}
      </Text>
    </View>
  );
}

export function ProductInterestField({
  value,
  valueReference,
  suggestion,
  suggestionReference,
  suggestionConfidence,
  onSave,
  isSaving = false,
}: Readonly<ProductInterestFieldProps>) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const currentValue = displayName(value, valueReference);
  const valueUnavailable = valueReference?.available === false;
  const suggestedName = displayName(suggestion, suggestionReference);
  const suggestionUnavailable = suggestionReference?.available === false;
  const valueKind = availableKind(valueReference);
  const suggestionKind = availableKind(suggestionReference);
  const kindLabel = (kind: LeadProductReference['kind']) =>
    kind ? t(`onboardingWizard.products.kind.${kind}`) : '';
  const showSuggestion = currentValue.trim() === '' && suggestedName !== '';
  const isClosestFit = suggestionConfidence === 'low';
  const suggestionHint = t(
    isClosestFit
      ? 'leads.detail.productInterestEditor.closestSuggestionHint'
      : 'leads.detail.productInterestEditor.suggestionHint',
  );
  const suggestionLabelParts = [
    suggestedName,
    suggestionKind ? kindLabel(suggestionKind) : '',
    isClosestFit ? suggestionHint : '',
  ].filter(Boolean);
  const suggestionAccessibilityLabel =
    suggestionLabelParts.length > 1 ? suggestionLabelParts.join(', ') : undefined;

  const startEditing = () => {
    setDraft(currentValue);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setDraft('');
  };

  const saveDraft = () => {
    if (isSaving) return;
    const next = draft.trim();
    setIsEditing(false);
    setDraft('');
    if (next === currentValue) return;
    onSave(next);
  };

  const confirmSuggestion = () => {
    if (isSaving) return;
    if (suggestionReference?.available === true) {
      onSave(suggestedName, suggestionReference.id);
      return;
    }
    onSave(suggestedName);
  };

  return (
    <HStack spacing="md" align="center" testID="lead_product_interest_field">
      <Package size={16} color={colors.text.tertiary} strokeWidth={1.5} />
      <VStack spacing="xxs" style={{ flex: 1 }}>
        <Text
          testID="lead_product_interest_label"
          variant="label"
          uppercase
          color={colors.text.tertiary}
        >
          {t('leads.detail.productInterest')}
        </Text>
        {isEditing ? (
          <HStack spacing="xs" align="center">
            <View style={{ flex: 1 }}>
              <FormInput
                testID="lead_product_interest_input"
                value={draft}
                onChangeText={setDraft}
                onSubmitEditing={saveDraft}
                editable={!isSaving}
                autoFocus
                placeholder={t('leads.detail.productInterestEditor.placeholder')}
                placeholderTextColor={colors.text.tertiary}
                accessibilityLabel={t('leads.detail.productInterestEditor.inputLabel')}
                returnKeyType="done"
                style={styles.input}
              />
            </View>
            <Pressable
              testID="lead_product_interest_save"
              onPress={saveDraft}
              disabled={isSaving}
              hitSlop={6}
              style={({ pressed }) => [
                styles.actionButton,
                pressed && styles.actionButtonPressed,
                isSaving && styles.actionButtonDisabled,
              ]}
              accessibilityRole="button"
              accessibilityState={{ disabled: isSaving }}
              accessibilityLabel={t('leads.detail.productInterestEditor.saveLabel')}
            >
              <Check size={16} color={colors.primary[500]} strokeWidth={2} />
            </Pressable>
            <Pressable
              testID="lead_product_interest_cancel"
              onPress={cancelEditing}
              hitSlop={6}
              style={({ pressed }) => [styles.actionButton, pressed && styles.actionButtonPressed]}
              accessibilityRole="button"
              accessibilityLabel={t('leads.detail.productInterestEditor.cancelLabel')}
            >
              <X size={16} color={colors.text.secondary} strokeWidth={2} />
            </Pressable>
          </HStack>
        ) : (
          <HStack spacing="xs" align="center">
            {showSuggestion ? (
              <VStack spacing="xxs" style={{ flex: 1 }}>
                <HStack spacing="xs" align="center">
                  <Text
                    testID="lead_product_interest_suggestion"
                    variant="body"
                    color={colors.text.primary}
                    accessibilityLabel={suggestionAccessibilityLabel}
                  >
                    {suggestedName}
                  </Text>
                  {suggestionKind && (
                    <KindBadge
                      labelTestID="lead_product_suggestion_kind"
                      label={kindLabel(suggestionKind)}
                    />
                  )}
                </HStack>
                <Text
                  testID="lead_product_interest_suggestion_hint"
                  variant="caption"
                  color={colors.text.secondary}
                >
                  {suggestionHint}
                </Text>
                {suggestionUnavailable && (
                  <Text
                    testID="lead_product_suggestion_unavailable"
                    variant="caption"
                    color={colors.text.secondary}
                  >
                    {t('leads.detail.productInterestEditor.unavailable')}
                  </Text>
                )}
              </VStack>
            ) : (
              <VStack spacing="xxs" style={{ flex: 1 }}>
                <HStack spacing="xs" align="center">
                  <Text
                    testID="lead_product_interest_value"
                    variant="body"
                    color={currentValue ? colors.text.primary : colors.text.tertiary}
                    accessibilityLabel={
                      valueKind ? `${currentValue}, ${kindLabel(valueKind)}` : undefined
                    }
                  >
                    {currentValue || '—'}
                  </Text>
                  {valueKind && (
                    <KindBadge
                      labelTestID="lead_product_interest_kind"
                      label={kindLabel(valueKind)}
                    />
                  )}
                </HStack>
                {valueUnavailable && (
                  <Text
                    testID="lead_product_interest_unavailable"
                    variant="caption"
                    color={colors.text.secondary}
                  >
                    {t('leads.detail.productInterestEditor.unavailable')}
                  </Text>
                )}
              </VStack>
            )}
            {showSuggestion && (
              <Pressable
                testID="lead_product_interest_confirm"
                onPress={confirmSuggestion}
                disabled={isSaving}
                style={({ pressed }) => [
                  styles.confirmButton,
                  pressed && styles.actionButtonPressed,
                  isSaving && styles.actionButtonDisabled,
                ]}
                accessibilityRole="button"
                accessibilityState={{ disabled: isSaving }}
                accessibilityLabel={t('leads.detail.productInterestEditor.confirmSuggestionLabel')}
              >
                <Check size={16} color={colors.primary[500]} strokeWidth={2} />
                <Text variant="caption" color={colors.primary[500]}>
                  {t('leads.detail.productInterestEditor.confirmSuggestion')}
                </Text>
              </Pressable>
            )}
            <Pressable
              testID="lead_product_interest_edit"
              onPress={startEditing}
              hitSlop={6}
              style={({ pressed }) => [styles.actionButton, pressed && styles.actionButtonPressed]}
              accessibilityRole="button"
              accessibilityLabel={t('leads.detail.productInterestEditor.editLabel')}
            >
              <Pencil size={14} color={colors.text.secondary} strokeWidth={2} />
            </Pressable>
          </HStack>
        )}
      </VStack>
    </HStack>
  );
}

const MIN_TARGET = Platform.select({
  android: tokens.interaction.minimumTarget.android,
  default: tokens.interaction.minimumTarget.default,
});

const styles = StyleSheet.create({
  input: {
    borderWidth: 1,
    borderColor: colors.border.default,
    borderRadius: tokens.radius.md,
    backgroundColor: colors.surface.row,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: tokens.spacing.xs,
    fontSize: 14,
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.border.default,
    backgroundColor: colors.surface.row,
    justifyContent: 'center',
    alignItems: 'center',
  },
  confirmButton: {
    minHeight: MIN_TARGET,
    minWidth: MIN_TARGET,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.xxs,
    paddingHorizontal: tokens.spacing.sm,
    borderRadius: tokens.radius.md,
    borderWidth: 1,
    borderColor: colors.primary[500],
    backgroundColor: colors.surface.row,
  },
  kindBadge: {
    borderWidth: 1,
    borderColor: colors.border.default,
    borderRadius: tokens.radius.md,
    paddingHorizontal: tokens.spacing.xs,
  },
  actionButtonPressed: {
    opacity: 0.6,
  },
  actionButtonDisabled: {
    opacity: 0.4,
  },
});
